import ConfirmDocumentField from '@Application/Beneficiaries/Documents/Commands/ConfirmDocumentField';
import type { CorrectDocumentFieldValue } from '@Application/Beneficiaries/Documents/Commands/CorrectDocumentField';
import type { DocumentFieldCorrectionFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentFieldCorrectionFailed';
import confirmDocumentField from '@Application/Beneficiaries/Documents/confirmDocumentField';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import FieldValueDisplay from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/SectionTabs/SectionPanel/DocumentEntry/FieldCard/FIeldValueDisplay/FieldValueDisplay';
import useFieldCorrection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/SectionTabs/SectionPanel/DocumentEntry/FieldCard/Hooks/useFieldCorrection';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import diffShownFields, {
	type ShownChange,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/diffShownFields';
import { openDocumentPanel } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import editingFieldSignal, {
	setEditingField,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import FieldConditionsHint from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FieldConditionsHint';
import FieldOccurrencesHint from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FieldOccurrencesHint';
import FieldPanelActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FieldPanelActions';
import FieldPanelMessages from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FieldPanelMessages';
import FieldReviewTrace from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FieldReviewTrace';
import FieldSources from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FieldSources';
import FieldValueControl from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FieldValueControl';
import FieldValueFormData, {
	type FieldValueDraft,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FormData/FieldValueFormData';
import describeSaveOutcome from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/Helpers/describeSaveOutcome';
import useFocusFieldControl from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/Hooks/useFocusFieldControl';
import useFieldViewerReader from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/Hooks/useFieldViewerReader';
import FieldVerificationBadge from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/FieldVerificationBadge';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import { css } from '@emotion/react';
import type { ViewerFieldInfo } from '@verclam/docx-kit-editor';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import * as React from 'react';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Form, Message } from '@verclam/ui-kit';

const LONG_TEXT_THRESHOLD = 80;

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const headerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

const titleStyle = css`
	margin: 0;
	${theme.typography.cardTitle}
	color: ${theme.colors.ink};
`;

const sectionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

const badgesStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.375rem;
`;

const invalidChipStyle = css`
	display: inline-flex;
	align-items: center;
	padding: 0.375rem 0.6875rem;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.states.danger.solid};
	color: ${theme.colors.surface};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
`;

const valueBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const valueLabelStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const readOnlyValueStyle = css`
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

type Notice = { readonly text: string; readonly change: ShownChange | null };

type PendingChange = { readonly before: readonly ViewerFieldInfo[]; readonly wasVerified: boolean };

type Props = {
	readonly field: DocumentFieldView;
};

/**
 * The content of the field panel for one field. Every modification, a choice or a boolean included,
 * goes through a single draft (`FieldValueFormData`), mirrored in `editingFieldSignal` while it is
 * modified so that the screen's guards can protect it. Keyed by field in `FieldPanel`: a new field is a
 * new draft.
 */
const FieldPanelContent: React.FC<Props> = ({ field }) => {
	const dispatch = useDispatch();
	const { facilityId, subject, documentId } = useDocumentRoute();
	const canEdit = useCanEditDocument();
	const document = useSelector((state) => state.documentsStore.document);
	const { handleRef, isViewerReady, revealChange } = useDocumentViewer();
	const { correct, isSubmitting } = useFieldCorrection(field.key);
	const readViewer = useFieldViewerReader(field.key);

	const [formData, setFormData] = React.useState(() => FieldValueFormData.createFor(field));
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
	const [notice, setNotice] = React.useState<Notice | null>(null);
	const [pendingChange, setPendingChange] = React.useState<PendingChange | null>(null);
	const [isConfirming, setIsConfirming] = React.useState(false);
	const controlRef = React.useRef<HTMLDivElement | null>(null);
	const retryRef = React.useRef<(() => void) | null>(null);
	// The panel is keyed by field: each field opened, from any gesture, lands the focus in its control.
	const handleComplete = useFocusFieldControl(controlRef, canEdit);

	// A long text is decided once, from what the field held on opening: swapping the control while typing would drop the focus.
	const [isLongText] = React.useState(() => {
		const saved = typeof field.value === 'string' ? field.value : '';

		return (
			saved.length > LONG_TEXT_THRESHOLD ||
			saved.includes('\n') ||
			(field.description ?? '').length > LONG_TEXT_THRESHOLD
		);
	});

	const isDirty = formData.isDirty;
	const isDocumentVerified = document?.status.value === 'verified';
	const sectionTitle = document?.findSectionByFieldKey(field.key)?.title ?? null;

	const reading = React.useMemo(() => (isViewerReady ? readViewer() : null), [isViewerReady, readViewer]);
	const conditionalPassages = reading?.info?.usedBy.conditions.length ?? 0;
	const drivenLabels = React.useMemo(
		() => [
			...new Set([
				...(reading?.info?.usedBy.computed.map((computed) => computed.label) ?? []),
				...(reading?.info?.usedBy.loops.map((loop) => loop.name) ?? []),
			]),
		],
		[reading],
	);

	// The saved value moved (confirmation, server-side change, our own save): an untouched draft follows it.
	React.useEffect(() => {
		setFormData((current) => (current.isDirty ? current : FieldValueFormData.createFor(field)));
	}, [field]);

	React.useEffect(() => {
		if (isDirty) {
			setEditingField({ fieldKey: field.key, isDirty: true });
			return;
		}

		if (editingFieldSignal.peek()?.fieldKey === field.key) {
			setEditingField(null);
		}
	}, [isDirty, field.key]);

	React.useEffect(
		() => () => {
			if (editingFieldSignal.peek()?.fieldKey === field.key) {
				setEditingField(null);
			}
		},
		[field.key],
	);

	// Once the store holds the saved document, one frame later the viewer has re-rendered it: compare what it shows now.
	React.useEffect(() => {
		if (pendingChange === null) {
			return undefined;
		}

		const frame = window.requestAnimationFrame(() => {
			const after = handleRef.current?.fields() ?? [];
			const { appeared, disappeared } = diffShownFields(pendingChange.before, after);

			setNotice({
				text: describeSaveOutcome(appeared.length, disappeared.length, pendingChange.wasVerified),
				change: appeared[0] ?? null,
			});
			setPendingChange(null);

			if (isViewerReady && readViewer().occurrenceCount === 0) {
				openDocumentPanel({ type: 'gone', reason: 'field' });
			}
		});

		return () => {
			window.cancelAnimationFrame(frame);
		};
	}, [pendingChange, handleRef, isViewerReady, readViewer]);

	const submit = React.useCallback(
		(value: CorrectDocumentFieldValue) => {
			const before = handleRef.current?.fields() ?? [];

			retryRef.current = () => {
				submit(value);
			};
			setErrorMessage(null);
			setNotice(null);

			correct(value)
				.then(() => {
					setFormData((current) => current.markSaved());
					setPendingChange({ before, wasVerified: isDocumentVerified });
				})
				.catch((action: DocumentFieldCorrectionFailedAction) => {
					setErrorMessage(
						action.payload?.message ??
							"L'enregistrement a échoué. Votre modification est conservée ici, réessayez.",
					);
					updateValidationFromExternalSource({
						action,
						eventNames: 'DOCUMENT_FIELD_CORRECTION_FAILED',
						formDataUpdater: setFormData,
					});
				});
		},
		[handleRef, correct, isDocumentVerified],
	);

	const handleChange = React.useCallback((value: FieldValueDraft) => {
		setNotice(null);
		setErrorMessage(null);
		setFormData((current) => current.setValue(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		submit(formData.toValue());
	}, [formData, submit]);

	const handleCancel = React.useCallback(() => {
		setErrorMessage(null);
		setFormData(FieldValueFormData.createFor(field));
	}, [field]);

	const handleClear = React.useCallback(() => {
		setFormData((current) => current.clear());
	}, []);

	const handleLeaveEmpty = React.useCallback(() => {
		submit(null);
	}, [submit]);

	const handleConfirm = React.useCallback(() => {
		setIsConfirming(true);
		dispatch(confirmDocumentField(new ConfirmDocumentField(facilityId, subject, documentId, field.key)))
			.catch(ignoreHandledError)
			.finally(() => {
				setIsConfirming(false);
			});
	}, [dispatch, facilityId, subject, documentId, field.key]);

	const handleRetry = React.useCallback(() => {
		retryRef.current?.();
	}, []);

	const handleReveal = React.useCallback(() => {
		if (notice?.change) {
			revealChange(notice.change);
		}
	}, [notice, revealChange]);

	return (
		<div css={bodyStyle}>
			<div css={headerStyle}>
				<h2 css={titleStyle}>{field.label}</h2>
				<ConditionalLoader data={sectionTitle}>
					{(title) => <p css={sectionStyle}>Section : {title}</p>}
				</ConditionalLoader>
			</div>
			<div css={badgesStyle}>
				<FieldVerificationBadge status={field.verification} />
				<ConditionalLoader data={field.isRejectedValue}>
					<span css={invalidChipStyle}>Valeur invalide</span>
				</ConditionalLoader>
			</div>
			<ConditionalLoader data={field.isRejectedValue}>
				<Message
					severity="error"
					text="La valeur proposée a été refusée par le gabarit : corrigez-la, ou laissez le champ vide, pour pouvoir valider le document."
				/>
			</ConditionalLoader>

			<ConditionalLoader data={canEdit}>
				<FieldConditionsHint hasConditionalPassages={conditionalPassages > 0} labels={drivenLabels} />
			</ConditionalLoader>

			<Form onSubmit={handleSubmit}>
				<div css={bodyStyle}>
					<div css={valueBlockStyle}>
						<span css={valueLabelStyle}>Valeur</span>
						{canEdit ? (
							<FieldValueControl
								field={field}
								formData={formData}
								isLongText={isLongText}
								isDisabled={isSubmitting}
								onChange={handleChange}
								containerRef={controlRef}
							/>
						) : (
							<div css={readOnlyValueStyle}>
								<FieldValueDisplay field={field} />
							</div>
						)}
					</div>

					<ConditionalLoader data={canEdit}>
						<FieldOccurrencesHint count={reading?.occurrenceCount ?? 0} />
					</ConditionalLoader>

					<FieldSources evidence={field.evidence} />
					<FieldReviewTrace field={field} />

					<ConditionalLoader data={canEdit}>
						<FieldPanelMessages
							isReopenWarningShown={isDirty && isDocumentVerified}
							errorMessage={errorMessage}
							onRetry={handleRetry}
							notice={notice?.text ?? null}
							onReveal={notice?.change ? handleReveal : null}
						/>
						<FieldPanelActions
							isDirty={isDirty}
							isSubmitting={isSubmitting}
							isConfirming={isConfirming}
							canLeaveEmpty={field.isRejectedValue}
							canConfirm={field.canConfirm}
							canComplete={field.isEmpty}
							canClear={!field.isEmpty && !formData.isBlank}
							onCancel={handleCancel}
							onConfirm={handleConfirm}
							onComplete={handleComplete}
							onClear={handleClear}
							onLeaveEmpty={handleLeaveEmpty}
						/>
					</ConditionalLoader>
				</div>
			</Form>
		</div>
	);
};

export default FieldPanelContent;
